import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    isSameDay,
    eachDayOfInterval,
    startOfWeek,
    endOfWeek,
} from "date-fns"

interface DatePickerProps {
    isOpen: boolean
    onClose: () => void
    onUpdate: (date: Date) => void
    initialDate?: Date
    label?: string
}

export function DatePicker({ isOpen, onClose, onUpdate, initialDate, label = "Select Date" }: DatePickerProps) {
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
        initialDate && !isNaN(initialDate.getTime()) ? initialDate : undefined
    )
    const [currentMonth, setCurrentMonth] = React.useState<Date>(
        initialDate && !isNaN(initialDate.getTime()) ? new Date(initialDate.getFullYear(), initialDate.getMonth()) : new Date()
    )

    // Update selected date when initialDate changes
    React.useEffect(() => {
        if (initialDate && !isNaN(initialDate.getTime())) {
            setSelectedDate(initialDate)
            setCurrentMonth(new Date(initialDate.getFullYear(), initialDate.getMonth()))
        }
    }, [initialDate])

    if (!isOpen) return null

    const handleDayClick = (day: Date) => {
        setSelectedDate(day)
    }

    const handleUpdate = () => {
        if (selectedDate) {
            onUpdate(selectedDate)
        }
        onClose()
    }

    const renderCalendar = (month: Date) => {
        const days = eachDayOfInterval({
            start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
            end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
        })

        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between px-2">
                    <button
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                        className="rounded-md border border-gray-200 p-2 bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                    >
                        <ChevronLeft strokeWidth={2.5} className="h-4 w-4 text-slate-700" />
                    </button>
                    <span className="text-sm font-bold">{format(month, "MMM, yyyy")}</span>
                    <button
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                        className="rounded-md border border-gray-200 p-2 bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                    >
                        <ChevronRight strokeWidth={2.5} className="h-4 w-4 text-slate-700" />
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-3">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                        <div key={d} className="text-center text-sm font-medium text-[#1F2937]/30">
                            {d}
                        </div>
                    ))}
                    {days.map((day, idx) => {
                        const isSelected = selectedDate && isSameDay(day, selectedDate)
                        const isOutsideMonth = month.getMonth() !== day.getMonth()

                        return (
                            <button
                                key={idx}
                                onClick={() => handleDayClick(day)}
                                className={`h-8 w-10 text-xs p-2 text-right font-medium transition-colors rounded-md ${
                                    isSelected
                                        ? "bg-[#ABD405] text-white font-bold"
                                        : isOutsideMonth
                                            ? "font-medium text-[#1F2937]/30"
                                            : "bg-[#F3F4F6]/30 border-2 border-gray-100 text-black hover:bg-gray-100"
                                }`}
                            >
                                {format(day, "d")}
                            </button>
                        )
                    })}
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="flex w-full max-w-xl overflow-hidden rounded-md bg-white shadow-2xl">
                {/* Main Content */}
                <div className="flex-1 p-6">
                    <div className="mb-8">
                        <label className="text-paragraph font-medium text-black mb-3 block">{label}</label>
                        <div className="rounded-md border font-normal p-2 text-sm text-[#9CA3AF] bg-white shadow">
                            {selectedDate && !isNaN(selectedDate.getTime()) ? format(selectedDate, "MM/dd/yyyy") : "mm/dd/yy"}
                        </div>
                    </div>

                    <div className="flex justify-center">
                        {renderCalendar(currentMonth)}
                    </div>

                    <div className="mt-8 flex items-center justify-between pt-5">
                            <button
                                onClick={onClose}
                                className="rounded-md border px-6 py-2 text-sm font-semibold bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!selectedDate}
                                className="btn-cta rounded-md px-6 py-2 text-sm font-semibold disabled:opacity-50"
                            >
                                Update
                            </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

